import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../auth/AuthProvider';
import { supabase } from '../../../lib/supabaseClient';

const ProofLikeButton = ({ proofId, size = 'md' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const classes = useMemo(() => {
    const base = 'inline-flex items-center gap-2 rounded-md border transition-gaming';
    const pad = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';

    if (isLiked) {
      return `${base} ${pad} bg-destructive text-destructive-foreground border-destructive hover:bg-destructive/90`;
    }

    return `${base} ${pad} bg-muted/40 text-foreground border-border hover:bg-muted`;
  }, [isLiked, size]);

  const iconSize = size === 'sm' ? 16 : 18;

  const load = useCallback(async () => {
    if (!proofId) return;

    try {
      const [{ count }, { data: mine }] = await Promise.all([
        supabase
          .from('ch_proof_likes')
          .select('id', { count: 'exact', head: true })
          .eq('proof_id', proofId),
        user?.id
          ? supabase
            .from('ch_proof_likes')
            .select('id')
            .eq('proof_id', proofId)
            .eq('user_id', user.id)
            .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      setLikeCount(Number(count || 0));
      setIsLiked(!!mine?.id);
    } catch {
      setLikeCount(0);
      setIsLiked(false);
    }
  }, [proofId, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async () => {
    if (!proofId) return;

    if (!user?.id) {
      navigate('/login-registration');
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    try {
      if (!isLiked) {
        const { error } = await supabase.from('ch_proof_likes').insert({ proof_id: proofId, user_id: user.id });
        if (!error) {
          setIsLiked(true);
          setLikeCount((v) => v + 1);
        }
      } else {
        const { error } = await supabase
          .from('ch_proof_likes')
          .delete()
          .eq('proof_id', proofId)
          .eq('user_id', user.id);
        if (!error) {
          setIsLiked(false);
          setLikeCount((v) => Math.max(0, v - 1));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={classes}
      onClick={toggle}
      disabled={isLoading}
      aria-pressed={isLiked}
      aria-label={isLiked ? '좋아요 취소' : '좋아요'}
      title={isLiked ? '좋아요 취소' : '좋아요'}
    >
      <Icon
        name="Heart"
        size={iconSize}
        color="currentColor"
        className={isLiked ? 'fill-current' : ''}
      />
      <span className="font-medium">좋아요</span>
      <span className="font-semibold tabular-nums">{likeCount}</span>
    </button>
  );
};

export default ProofLikeButton;
